import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration, DoorGraphicTypes } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface GraphicsViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface DoorGraphicTypeViewProps {
	doorGraphicType: DoorGraphicTypes;
	isClosed?: boolean;
	onPanelOpenClose?: (isOpen: boolean) => void;
}

interface DoorGraphicItemsProps {
	doorGraphicType: DoorGraphicTypes;
}

interface GraphicsViewContextType {
	selectedDoorGraphicItemId: string;
	selectedDoorGraphicItemName: string;
	handleDoorGraphicItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function GraphicsView(props: GraphicsViewProps) {

	// initial values.
	const initialDoorGraphicItemId = Configuration.doorGraphicOption;
	const initialDoorGraphicItemName = () => getItemName(initialDoorGraphicItemId);

	// the roundel colours are presented as an accordion of one panel per colour, of which the panel holding the selected design is the one open on arrival.
	const initialOpenDoorGraphicType = () => CloudData.getDoorGraphicTypeForId(initialDoorGraphicItemId);

	// states.
	const [selectedDoorGraphicItemId, setSelectedDoorGraphicItemId] = useState(initialDoorGraphicItemId);
	const [selectedDoorGraphicItemName, setSelectedDoorGraphicItemName] = useState(initialDoorGraphicItemName);

	// the one roundel colour panel currently open, or null when the customer has closed them all, as the paintwork and wheels views hold
	// their own accordions. there is no separate 'selected roundel colour' any more - the colour is whichever one the selected design belongs to.
	const [openDoorGraphicType, setOpenDoorGraphicType] = useState<DoorGraphicTypes | null>(initialOpenDoorGraphicType);

	// handlers.

	const handlePanelOpenClose = (doorGraphicTypePanel: DoorGraphicTypes, isOpen: boolean) => {

		// only one panel is open at a time, so opening one closes the others, and closing one leaves none open.
		setOpenDoorGraphicType(isOpen ? doorGraphicTypePanel : null);
	};

	const handleDoorGraphicItemClicked = (id: string) => {

		if (Configuration.doorGraphicOption === id) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedDoorGraphicItemId(id);
		setSelectedDoorGraphicItemName(getItemName(id));

		// update the configuration.
		Configuration.doorGraphicOption = id;
		Configuration.updateCosts();

		// select the door graphic option on the car.
		props.exteriorConfigurator.selectDoorGraphicOption(id);
	};

	// return the HTML.
	return (
		<GraphicsViewContext.Provider value={{ selectedDoorGraphicItemId, selectedDoorGraphicItemName, handleDoorGraphicItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-graphics">
							GRAPHICS
						</h2>
					</div>

					{/* the roundel colours are an accordion of one panel each, holding that colour's designs, as the paintwork and wheels steps are. */}
					<DoorGraphicTypeView
						doorGraphicType={DoorGraphicTypes.Black}
						isClosed={openDoorGraphicType !== DoorGraphicTypes.Black}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(DoorGraphicTypes.Black, isOpen)}
					/>

					<DoorGraphicTypeView
						doorGraphicType={DoorGraphicTypes.Clear}
						isClosed={openDoorGraphicType !== DoorGraphicTypes.Clear}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(DoorGraphicTypes.Clear, isOpen)}
					/>

					<DoorGraphicTypeView
						doorGraphicType={DoorGraphicTypes.White}
						isClosed={openDoorGraphicType !== DoorGraphicTypes.White}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(DoorGraphicTypes.White, isOpen)}
					/>

				</div>
			</VehicleView>
		</GraphicsViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const GraphicsViewContext = createContext<GraphicsViewContextType | undefined>(undefined);

function DoorGraphicTypeView(props: DoorGraphicTypeViewProps) {

	// get the context and ensure it is valid.
	const context = useContext(GraphicsViewContext);
	if (!context) {
		throw new Error('GraphicsViewContext must be used within a GraphicsView component');
	}

	// return the HTML.
	// n.b. the name, price and info shown are the selected design's, repeated on every panel - they describe the configuration, so they
	// must not change as panels are opened and closed.
	return (
		<SectionPanel
			subHeading={getTypeName(props.doorGraphicType)}
			modalContent={CloudData.getModalContent(context.selectedDoorGraphicItemId, CloudData.graphicsOptions)}
			isClosed={props.isClosed}
			onPanelOpenClose={props.onPanelOpenClose}
		>
			<SelectedSectionItem
				itemName={context.selectedDoorGraphicItemName}
				sectionPrice={CloudData.getPrice(context.selectedDoorGraphicItemId, CloudData.graphicsOptions, { ref: false })}
			/>
			<DoorGraphicItems doorGraphicType={props.doorGraphicType} />
		</SectionPanel>
	);
}

function DoorGraphicItems(props: DoorGraphicItemsProps) {
	switch (props.doorGraphicType) {
		case DoorGraphicTypes.Black:
			return <DoorGraphicItemsBlack />;
		case DoorGraphicTypes.Clear:
			return <DoorGraphicItemsClear />;
		default:
			return <DoorGraphicItemsWhite />;
	}
};

function DoorGraphicItemsBlack() {
	return (
		<ViewContentRows maxRowLength={11}>
			<DoorGraphicItem id={'DK002'} imageFilePath={'black/black'} />
			<DoorGraphicItem id={'DK050'} imageFilePath={'black/0'} />
			<DoorGraphicItem id={'DK051'} imageFilePath={'black/1'} />
			<DoorGraphicItem id={'DK052'} imageFilePath={'black/2'} />
			<DoorGraphicItem id={'DK053'} imageFilePath={'black/3'} />
			<DoorGraphicItem id={'DK054'} imageFilePath={'black/4'} />
			<DoorGraphicItem id={'DK055'} imageFilePath={'black/5'} />
			<DoorGraphicItem id={'DK056'} imageFilePath={'black/6'} />
			<DoorGraphicItem id={'DK057'} imageFilePath={'black/7'} />
			<DoorGraphicItem id={'DK058'} imageFilePath={'black/8'} />
			<DoorGraphicItem id={'DK059'} imageFilePath={'black/9'} />
		</ViewContentRows>
	);
}

function DoorGraphicItemsClear() {
	return (
		<ViewContentRows maxRowLength={11}>
			<DoorGraphicItem id={'DK003'} imageFilePath={'clear/clear'} />
			<DoorGraphicItem id={'DK060'} imageFilePath={'clear/0'} />
			<DoorGraphicItem id={'DK061'} imageFilePath={'clear/1'} />
			<DoorGraphicItem id={'DK062'} imageFilePath={'clear/2'} />
			<DoorGraphicItem id={'DK063'} imageFilePath={'clear/3'} />
			<DoorGraphicItem id={'DK064'} imageFilePath={'clear/4'} />
			<DoorGraphicItem id={'DK065'} imageFilePath={'clear/5'} />
			<DoorGraphicItem id={'DK066'} imageFilePath={'clear/6'} />
			<DoorGraphicItem id={'DK067'} imageFilePath={'clear/7'} />
			<DoorGraphicItem id={'DK068'} imageFilePath={'clear/8'} />
			<DoorGraphicItem id={'DK069'} imageFilePath={'clear/9'} />
		</ViewContentRows>
	);
}

function DoorGraphicItemsWhite() {
	return (
		<ViewContentRows maxRowLength={11}>
			<DoorGraphicItem id={'DK001'} imageFilePath={'white/white'} />
			<DoorGraphicItem id={'DK040'} imageFilePath={'white/0'} />
			<DoorGraphicItem id={'DK041'} imageFilePath={'white/1'} />
			<DoorGraphicItem id={'DK042'} imageFilePath={'white/2'} />
			<DoorGraphicItem id={'DK043'} imageFilePath={'white/3'} />
			<DoorGraphicItem id={'DK044'} imageFilePath={'white/4'} />
			<DoorGraphicItem id={'DK045'} imageFilePath={'white/5'} />
			<DoorGraphicItem id={'DK046'} imageFilePath={'white/6'} />
			<DoorGraphicItem id={'DK047'} imageFilePath={'white/7'} />
			<DoorGraphicItem id={'DK048'} imageFilePath={'white/8'} />
			<DoorGraphicItem id={'DK049'} imageFilePath={'white/9'} />
		</ViewContentRows>
	);
}

function DoorGraphicItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(GraphicsViewContext);
	if (!context) {
		throw new Error('GraphicsViewContext must be used within a GraphicsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/door-graphic/${props.imageFilePath}.svg`}
			isSelected={props.id === context.selectedDoorGraphicItemId}
			onItemClicked={context.handleDoorGraphicItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.graphicsOptions);
}

// returns the heading for the given roundel colour's accordion panel.
// n.b. the enum's string value is the colour on its own, which needs qualifying to read as a heading.
function getTypeName(doorGraphicType: DoorGraphicTypes): string {
	return `${DoorGraphicTypes[doorGraphicType].toUpperCase()} ROUNDELS`;
}
